import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Facebook } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { SERVICES, SITE } from "@/data/site";

const empty = { name: "", email: "", phone: "", service: "", vehicle: "", preferred_date: "", message: "" };

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ ...empty, service: params.get("service") || "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error("Please pick a service.");
      return;
    }
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry sent — we'll be in touch shortly.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="border-b border-white/10 bg-[#111] relative grain">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <p className="label-industrial text-ember mb-4">Contact</p>
          <h1 className="font-display text-6xl lg:text-8xl tracking-wide text-white" data-testid="contact-heading">
            BOOK A JOB
          </h1>
          <p className="mt-6 text-gray-400 max-w-xl">
            Tell us what you're driving (or floating) and what it needs. We'll come back with a time that suits you.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 lg:grid-cols-5 gap-16">
        <form onSubmit={submit} className="lg:col-span-3 space-y-8" data-testid="booking-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div className="space-y-2">
              <Label htmlFor="name" className="label-industrial text-gray-400">Name *</Label>
              <Input id="name" data-testid="booking-name-input" required value={form.name} onChange={set("name")}
                className="bg-[#111] border-white/15 rounded-none h-12 focus-visible:ring-ember" placeholder="Your name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="label-industrial text-gray-400">Phone *</Label>
              <Input id="phone" data-testid="booking-phone-input" required value={form.phone} onChange={set("phone")}
                className="bg-[#111] border-white/15 rounded-none h-12 focus-visible:ring-ember" placeholder="027 000 0000" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div className="space-y-2">
              <Label htmlFor="email" className="label-industrial text-gray-400">Email *</Label>
              <Input id="email" type="email" data-testid="booking-email-input" required value={form.email} onChange={set("email")}
                className="bg-[#111] border-white/15 rounded-none h-12 focus-visible:ring-ember" placeholder="you@example.co.nz" />
            </div>
            <div className="space-y-2">
              <Label className="label-industrial text-gray-400">Service *</Label>
              <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                <SelectTrigger data-testid="booking-service-select" className="bg-[#111] border-white/15 rounded-none h-12 focus:ring-ember">
                  <SelectValue placeholder="Pick a service" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/15 rounded-none">
                  {SERVICES.map((s) => (
                    <SelectItem key={s.slug} value={s.slug} data-testid={`booking-service-option-${s.slug}`}>
                      {s.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div className="space-y-2">
              <Label htmlFor="vehicle" className="label-industrial text-gray-400">Vehicle / Boat</Label>
              <Input id="vehicle" data-testid="booking-vehicle-input" value={form.vehicle} onChange={set("vehicle")}
                className="bg-[#111] border-white/15 rounded-none h-12 focus-visible:ring-ember" placeholder="e.g. 1934 Ford Coupe" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="preferred_date" className="label-industrial text-gray-400">Preferred Date</Label>
              <Input id="preferred_date" type="date" data-testid="booking-date-input" value={form.preferred_date} onChange={set("preferred_date")}
                className="bg-[#111] border-white/15 rounded-none h-12 focus-visible:ring-ember [color-scheme:dark]" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="label-industrial text-gray-400">Tell us about the job *</Label>
            <Textarea id="message" data-testid="booking-message-input" required rows={5} value={form.message} onChange={set("message")}
              className="bg-[#111] border-white/15 rounded-none focus-visible:ring-ember" placeholder="What's it doing (or not doing)?" />
          </div>

          <button
            type="submit"
            disabled={sending}
            data-testid="booking-submit-btn"
            className="label-industrial bg-ember text-white px-10 py-5 hover:bg-ember-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
          >
            {sending ? "Sending..." : "Send Enquiry"}
          </button>
        </form>

        <aside className="lg:col-span-2 space-y-10" data-testid="contact-info-panel">
          <div className="border border-white/10 bg-[#111] p-8 space-y-6">
            <p className="label-industrial text-ember">The Workshop</p>
            <ul className="space-y-4 text-sm text-gray-300">
              <li className="flex items-start gap-3" data-testid="contact-address">
                <MapPin className="w-4 h-4 mt-0.5 text-ember shrink-0" /> {SITE.location}
              </li>
              <li className="flex items-start gap-3" data-testid="contact-phone">
                <Phone className="w-4 h-4 mt-0.5 text-ember shrink-0" /> {SITE.phone}
              </li>
              <li className="flex items-start gap-3" data-testid="contact-email">
                <Mail className="w-4 h-4 mt-0.5 text-ember shrink-0" /> {SITE.email}
              </li>
              <li data-testid="contact-facebook">
                <a href={SITE.facebook} target="_blank" rel="noopener noreferrer"
                  className="flex items-start gap-3 hover:text-ember transition-colors duration-150">
                  <Facebook className="w-4 h-4 mt-0.5 text-ember shrink-0" /> Follow us on Facebook
                </a>
              </li>
            </ul>
          </div>
          <div className="border border-white/10 bg-[#111] p-8">
            <p className="label-industrial text-ember mb-6">Opening Hours</p>
            <ul className="space-y-3 text-sm text-gray-300">
              {SITE.hours.map((h) => (
                <li key={h.days} className="flex items-start gap-3" data-testid={`contact-hours-${h.days.split(" ")[0].toLowerCase()}`}>
                  <Clock className="w-4 h-4 mt-0.5 text-ember shrink-0" />
                  <span className="flex-1 flex justify-between gap-4">
                    <span>{h.days}</span>
                    <span className="text-gray-500">{h.time}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="label-industrial text-ember mb-2">Find Us</p>
            <h2 className="font-display text-4xl lg:text-5xl tracking-wide text-white" data-testid="map-heading">
              205 AICKIN ROAD
            </h2>
          </div>
          <a
            href="https://www.google.com/maps/search/?api=1&query=205+Aickin+Road%2C+Whangamata+3620%2C+New+Zealand"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="map-directions-link"
            className="label-industrial text-gray-300 hover:text-ember transition-colors duration-150"
          >
            Get Directions
          </a>
        </div>
        <div className="border border-white/10" data-testid="contact-map">
          <iframe
            title="JCD Customs & Marine — 205 Aickin Road, Whangamata"
            src="https://www.google.com/maps?q=205%20Aickin%20Road%2C%20Whangamata%203620%2C%20New%20Zealand&output=embed"
            className="w-full h-[420px] grayscale-[55%] contrast-[1.05]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </section>
    </div>
  );
}
